import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { customInstance } from "@/lib/axios";
import { PASSWORD_MIN_LENGTH, validatePasswordClient } from "@/lib/password-requirements";
import { PasswordRequirementChecklist } from "@/components/PasswordRequirementChecklist";
import { AuthPageShell } from "@/components/layout/auth-page-shell";

export function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [touchedPassword, setTouchedPassword] = useState(false);
  const navigate = useNavigate();

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (touchedPassword) {
      setPasswordErrors(value ? validatePasswordClient(value) : []);
    } else {
      setPasswordErrors([]);
    }
    if (confirmPassword && value !== confirmPassword) {
      setConfirmError("Passwords do not match.");
    } else {
      setConfirmError(null);
    }
  };

  const handlePasswordBlur = () => {
    setTouchedPassword(true);
    if (password) setPasswordErrors(validatePasswordClient(password));
  };

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);
    setConfirmError(value && password !== value ? "Passwords do not match." : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error("Invalid or missing link. Please use the link from your invite email.");
      return;
    }

    const pwdErrors = validatePasswordClient(password);
    const matchErr = password !== confirmPassword ? "Passwords do not match." : null;

    setPasswordErrors(pwdErrors);
    setConfirmError(matchErr);
    setTouchedPassword(true);

    if (pwdErrors.length > 0 || matchErr) {
      toast.error("Please fix the errors below.");
      return;
    }

    setIsLoading(true);
    setPasswordErrors([]);
    setConfirmError(null);
    try {
      await customInstance.post("/api/auth/set-password-from-invite/", { token, password });
      toast.success("Password set successfully. You can now log in.");
      navigate("/login");
    } catch (err: any) {
      const messages = err?.response?.data?.password;
      const errorList = Array.isArray(messages) ? messages : [messages].filter(Boolean);
      const fallback = err?.response?.data?.token?.[0] ?? err?.message ?? "Failed to set password.";
      const toShow = errorList.length > 0 ? errorList : [fallback];
      setPasswordErrors(toShow);
      toast.error(toShow[0]);
    } finally {
      setIsLoading(false);
    }
  };

  const hasPasswordError = passwordErrors.length > 0;

  if (!token.trim()) {
    return (
      <AuthPageShell>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-xl">Invalid link</CardTitle>
            <CardDescription>
              This set-password link is missing or invalid. Please use the link from your invite email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Go to login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img src="/logo-full.png" alt="DealerDog" className="h-20 sm:h-24 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Set your password</CardTitle>
          <CardDescription className="text-center">
            You were invited to join DealerDog Finance. Choose a password to access the portal.
          </CardDescription>
          <p className="text-center text-sm pt-2">
            <Link to="/login" className="text-primary hover:underline">
              Back to login
            </Link>
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordRequirementChecklist password={password} />
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onBlur={handlePasswordBlur}
                required
                minLength={PASSWORD_MIN_LENGTH}
                aria-describedby="password-requirements password-errors"
                aria-invalid={hasPasswordError}
                className={hasPasswordError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {hasPasswordError && (
                <div id="password-errors" className="text-sm text-red-600 font-medium space-y-0.5" role="alert">
                  {passwordErrors.map((msg, i) => (
                    <p key={i}>{msg}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => handleConfirmChange(e.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
                aria-describedby={confirmError ? "confirm-error" : undefined}
                aria-invalid={!!confirmError}
                className={confirmError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {confirmError && (
                <p id="confirm-error" className="text-sm text-red-600 font-medium" role="alert">
                  {confirmError}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Setting password…" : "Set password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthPageShell>
  );
}
